/**
 * JSON-LD builders for /about-us/fin, /part and /filter (FRIDGE-244 · FRIDGE-396).
 *
 * Pure functions, imported by the two page components and by
 * tests/fin-parts-jsonld.test.ts, so the emitted nodes are verifiable without
 * a build or a server.
 *
 * Hard constraint (FRIDGE-131 / the GSC +200% Product-error class): every
 * field mirrors content the page visibly renders — no Product nodes on list
 * pages, no prices, no ratings, nothing the page does not show. Omit when
 * absent, never fabricate.
 *
 * Page-level regular <script> tags are required (see
 * components/seo/static-page-schemas.tsx): the root layout's Organization and
 * WebSite schemas ride next/script (afterInteractive) and never reach the
 * server HTML, which is why the 08/03 registry sweep measured these pages at
 * zero parseable blocks.
 */

import { staticContentLastmod } from './static-content-freshness'
import { newestIso } from './sitemap-lastmod'
import { buildBreadcrumbSchema } from '@/lib/structured-data'
import { organizationRef } from '@/lib/site-entity'
import { IDENTIFIER_DEFINITIONS } from '@/lib/identifiers'
import { FIN_NOT_ACCOUNT_OR_ORDER } from '@/lib/brand-facts'
import { filterModelHubBreadcrumbs, filterModelsBreadcrumbs, filterStoreBreadcrumbs, partsBreadcrumbs } from '@/lib/breadcrumbs'
import { filterPagePath, partPagePath, PARTS_ROUTE_ROOT, FILTERS_ROUTE_ROOT } from '@/lib/parts/fpn'

import { FIS_SCOPE_SUMMARY, FIS_DIRECTION_BY_SCOPE } from '@/lib/fis-factors'

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://fridge.com'

// FRIDGE-855 — the ONE Organization, referenced by @id, not a lookalike
// re-declaration. Seven builders in this file publish through it.
const ORGANIZATION = organizationRef(SITE_URL)

/**
 * /about-us/fin — WebPage whose mainEntity is the DefinedTerm the page exists to
 * define. The DefinedTerm description mirrors the page's own definition copy
 * (hero + definition sections); the name keeps the abbreviation in front —
 * FIN, then the expansion — per the standing naming rule. The page also
 * defines the (FPN) Fridge.com Part Number (FRIDGE-333) in its own visible
 * section (#fpn) and the (FFN) Fridge.com Filter Number in its own (#ffn), so
 * both terms ride along under `mentions` — FIN, FPN, FFN, the order the
 * identifiers are always named in.
 */
export function buildAboutFinPageJsonLd(name: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${SITE_URL}/about-us/fin`,
    dateModified: staticContentLastmod('/about-us/fin') ?? undefined,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    publisher: ORGANIZATION,
    mainEntity: {
      '@type': 'DefinedTerm',
      '@id': `${SITE_URL}/about-us/fin#fin`,
      name: 'FIN — Fridge.com Identification Number',
      termCode: 'FIN',
      url: `${SITE_URL}/about-us/fin`,
      inDefinedTermSet: { '@id': `${SITE_URL}/about-us#identifiers` },
      // The canonical definition leads (lib/identifiers.ts — one version
      // everywhere, owner ruling 08/22/2026); mechanics follow it.
      // FRIDGE-899 — the correction rides the DefinedTerm itself, because this
      // is the node an answer engine reads when it cites this page, and one of
      // them read it and reported the FIN as an account/order number tied to
      // warranties and returns. The page visibly renders the same sentence
      // (the STRAIGHT ANSWERS card), so the mirror-what-the-page-shows rule at
      // the top of this file holds. The canonical definition still leads.
      description:
        IDENTIFIER_DEFINITIONS.FIN +
        ' Format FIN- followed by seven zero-padded digits; FIN-0042893 is an illustrative format example, not a catalog link. Assigned once, ' +
        'never changed, never reused. ' +
        FIN_NOT_ACCOUNT_OR_ORDER,
    },
    mentions: [buildFpnDefinedTerm(), buildFfnDefinedTerm()],
  }
}

/**
 * The (FPN) Fridge.com Part Number as a DefinedTerm — the part-side twin of the
 * FIN term above, anchored at /about-us/fin#fpn where the page visibly defines it.
 * Mirrors that section's copy; nothing here that the page does not say.
 */
export function buildFpnDefinedTerm() {
  return {
    '@type': 'DefinedTerm',
    '@id': `${SITE_URL}/about-us/fpn#term`,
    name: 'FPN — Fridge.com Part Number',
    termCode: 'FPN',
    url: `${SITE_URL}/about-us/fpn`,
    inDefinedTermSet: { '@id': `${SITE_URL}/about-us#identifiers` },
    description:
      IDENTIFIER_DEFINITIONS.FPN +
      ' Format FPN- followed by seven zero-padded digits, for example FPN-0000012, listed on /parts. ' +
      'A part carries an FPN and never a FIN; a water filter is not a part and carries an FFN instead. ' +
      'An FPN is minted once and never changes or gets reused.',
  }
}

/**
 * The (FFN) Fridge.com Filter Number as a DefinedTerm — the filter-side
 * sibling, anchored at /about-us/fin#ffn where the page visibly defines it
 * (FRIDGE-396: a filter is not a part). Mirrors that section's copy; nothing
 * here that the page does not say.
 */
export function buildFfnDefinedTerm() {
  return {
    '@type': 'DefinedTerm',
    '@id': `${SITE_URL}/about-us/ffn#term`,
    name: 'FFN — Fridge.com Filter Number',
    termCode: 'FFN',
    url: `${SITE_URL}/about-us/ffn`,
    inDefinedTermSet: { '@id': `${SITE_URL}/about-us#identifiers` },
    description:
      IDENTIFIER_DEFINITIONS.FFN +
      ' Format FFN- followed by seven zero-padded digits, for example FFN-0000012, listed in the filter ' +
      'catalog at /filters. A filter is not a part: a filter carries an FFN and never an FPN. An FFN is ' +
      'assigned once and never changes or gets reused.',
  }
}

/**
 * FIS as a DefinedTerm — the fourth system and the only score among the four
 * (hub-and-spoke build, 08/21/2026). Anchored at its own spoke, /about-us/fis.
 */
export function buildFisDefinedTerm() {
  return {
    '@type': 'DefinedTerm',
    '@id': `${SITE_URL}/about-us/fis#term`,
    name: 'FIS — Fridge.com Intelligence Score',
    termCode: 'FIS',
    url: `${SITE_URL}/about-us/fis`,
    inDefinedTermSet: { '@id': `${SITE_URL}/about-us#identifiers` },
    description:
      IDENTIFIER_DEFINITIONS.FIS +
      ' ' + FIS_SCOPE_SUMMARY + ' ' + FIS_DIRECTION_BY_SCOPE + ' FIS is a score, not an ' +
      'identifier: it rates places and never products. The four Fridge.com systems are always named ' +
      'in the order FIN, FPN, FFN, FIS — three permanent identifiers, then this one score.',
  }
}

/**
 * The DefinedTermSet for /about-us — the hub node the four terms
 * declare membership in. hasDefinedTerm carries the four full nodes so the hub
 * page is self-contained for a crawler that fetches only it.
 */
export function buildIdentifierTermSetNode() {
  const finTerm = {
    '@type': 'DefinedTerm',
    '@id': `${SITE_URL}/about-us/fin#fin`,
    name: 'FIN — Fridge.com Identification Number',
    termCode: 'FIN',
    url: `${SITE_URL}/about-us/fin`,
    inDefinedTermSet: { '@id': `${SITE_URL}/about-us#identifiers` },
    description:
      IDENTIFIER_DEFINITIONS.FIN +
      ' Format FIN- followed by seven zero-padded digits; assigned once, never changed, never reused.',
  }
  return {
    '@type': 'DefinedTermSet',
    '@id': `${SITE_URL}/about-us#identifiers`,
    name: 'The Fridge.com identifier system — FIN, FPN, FFN, FIS',
    url: `${SITE_URL}/about-us`,
    hasDefinedTerm: [finTerm, buildFpnDefinedTerm(), buildFfnDefinedTerm(), buildFisDefinedTerm()],
  }
}

/**
 * WebPage node for one identifier spoke (/about-us/fpn · /about-us/ffn · /about-us/fis):
 * mainEntity is that spoke's DefinedTerm, mentions carries its three siblings
 * so every spoke names the whole family in canonical order.
 */
export function buildIdentifierSpokeJsonLd(
  path: '/about-us/fpn' | '/about-us/ffn' | '/about-us/fis',
  name: string,
  description: string,
) {
  const term =
    path === '/about-us/fpn' ? buildFpnDefinedTerm() : path === '/about-us/ffn' ? buildFfnDefinedTerm() : buildFisDefinedTerm()
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${SITE_URL}${path}`,
    dateModified: staticContentLastmod(path) ?? undefined,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    publisher: ORGANIZATION,
    mainEntity: term,
  }
}

export interface PartsListedItem {
  title: string
  slug: string
}

/**
 * FRIDGE-903 — the `dateModified` fragment for a list page's CollectionPage
 * node: the newest member revision the page rendered (memberSetLastmod) or
 * the newest hub revision, the same value the page's sitemap family emits as
 * <lastmod>. Nothing real → nothing emitted.
 */
function datedNode(dateModified: string | null | undefined): { dateModified?: string } {
  const iso = newestIso(dateModified)
  return iso ? { dateModified: iso } : {}
}

/**
 * /part — CollectionPage; mainEntity is an ItemList of exactly the product
 * cards the page renders, in DOM order. ListItem carries name + url ONLY —
 * list pages never embed Product nodes — and an empty list omits mainEntity
 * rather than asserting an empty ItemList. Every url is the part's home,
 * /part/<slug> (FRIDGE-333 · FRIDGE-389) — the same href the visible card carries.
 */
export function buildPartsPageJsonLd(name: string, description: string, items: PartsListedItem[], dateModified?: string | null) {
  const page = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${PARTS_ROUTE_ROOT}`,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
  }
  if (items.length === 0) return page
  return {
    ...page,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.title,
        url: `${SITE_URL}${partPagePath(p.slug)}`,
      })),
    },
  }
}

/**
 * Two levels, Fridge.com / Parts — built from the SAME trail the page renders
 * as its visible breadcrumb (lib/breadcrumbs.ts partsBreadcrumbs, FRIDGE-269),
 * through the one BreadcrumbList builder every page uses (the leaf is the
 * current page and carries no `item`, like every other page's schema).
 */
export function buildPartsBreadcrumbJsonLd() {
  return buildBreadcrumbSchema(partsBreadcrumbs())
}

/**
 * /filter — CollectionPage for the filter catalog, the exact shape of
 * buildPartsPageJsonLd with the filter root: ListItem name + url only, list
 * pages never embed Product nodes, an empty list omits mainEntity. Every url
 * is the filter's home, /filter/<slug> (FRIDGE-396).
 */
export function buildFilterStorePageJsonLd(name: string, description: string, items: PartsListedItem[], dateModified?: string | null) {
  const page = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${FILTERS_ROUTE_ROOT}`,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
  }
  if (items.length === 0) return page
  return {
    ...page,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.title,
        url: `${SITE_URL}${filterPagePath(p.slug)}`,
      })),
    },
  }
}

/** Two levels, Fridge.com / Filters — the /filter hub's visible trail, through the one builder. */
export function buildFilterStoreBreadcrumbJsonLd() {
  return buildBreadcrumbSchema(filterStoreBreadcrumbs())
}

/**
 * /filters/models/[code] — the per-model filter hub (FRIDGE-722). A
 * CollectionPage whose mainEntity is an ItemList of exactly the filter cards
 * the page renders, in DOM order (manufacturer cartridge first, then the
 * compatible replacements). ListItem carries name + url ONLY: a list page
 * never embeds Product nodes, prices, offers or ratings (the FRIDGE-131 GSC
 * class) — the price comparison lives on each filter's own page. `about`
 * names the OEM code as a DefinedTerm in the manufacturer's numbering, which
 * the page's identity block visibly states. An empty list omits mainEntity.
 */
export function buildFilterModelHubJsonLd(
  hub: { code: string; brand: string; slug: string; name: string | null },
  pageName: string,
  description: string,
  items: PartsListedItem[],
  dateModified?: string | null
) {
  const url = `${SITE_URL}${FILTERS_ROUTE_ROOT}/models/${hub.slug}`
  const page: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageName,
    description,
    url,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
    about: {
      '@type': 'DefinedTerm',
      name: `${hub.brand} ${hub.code}`,
      termCode: hub.code,
      ...(hub.name ? { alternateName: hub.name } : {}),
      inDefinedTermSet: { '@type': 'DefinedTermSet', name: `${hub.brand} refrigerator filter part numbers` },
    },
  }
  if (items.length === 0) return page
  return {
    ...page,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.title,
        url: `${SITE_URL}${filterPagePath(p.slug)}`,
      })),
    },
  }
}

/** Four levels, Fridge.com / Filters / Filter Numbers / {Brand CODE} — the hub's visible trail. */
export function buildFilterModelHubBreadcrumbJsonLd(model: { brand: string; code: string }) {
  return buildBreadcrumbSchema(filterModelHubBreadcrumbs(model))
}

/**
 * /filters/models — the hub index: CollectionPage + ItemList of the shipped
 * hubs (name + url only), in the order the page lists them.
 */
export function buildFilterModelsIndexJsonLd(name: string, description: string, hubs: Array<{ title: string; slug: string }>, dateModified?: string | null) {
  const page = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${FILTERS_ROUTE_ROOT}/models`,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
  }
  if (hubs.length === 0) return page
  return {
    ...page,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: hubs.length,
      itemListElement: hubs.map((h, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: h.title,
        url: `${SITE_URL}${FILTERS_ROUTE_ROOT}/models/${h.slug}`,
      })),
    },
  }
}

/** Three levels, Fridge.com / Filters / Filter Numbers — the index's visible trail. */
export function buildFilterModelsBreadcrumbJsonLd() {
  return buildBreadcrumbSchema(filterModelsBreadcrumbs())
}

/**
 * /filters/cross-reference (FRIDGE-731): CollectionPage whose mainEntity is a
 * DefinedTermSet — one DefinedTerm per filter number (termCode + name, url
 * only when the family page ships). Never Product nodes on a list page.
 */
export function buildFilterCrossReferenceJsonLd(name: string, description: string, rows: Array<{ code: string; brand: string; name: string | null; hubPath: string | null }>, dateModified?: string | null) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${FILTERS_ROUTE_ROOT}/cross-reference`,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
    mainEntity: {
      '@type': 'DefinedTermSet',
      name: 'Refrigerator filter numbers',
      hasDefinedTerm: rows.map((r) => ({
        '@type': 'DefinedTerm',
        termCode: r.code,
        name: r.name ? `${r.brand} ${r.code} (${r.name})` : `${r.brand} ${r.code}`,
        ...(r.hubPath ? { url: `${SITE_URL}${r.hubPath}` } : {}),
      })),
    },
  }
}

/**
 * /filters/<brand>-refrigerator-water-filters — the code-governed brand
 * filter page (FRIDGE-724): CollectionPage + ItemList of exactly the filter
 * cards the page renders (name + url only — never Product nodes on a list
 * page); `about` names the brand. An empty list omits mainEntity.
 */
export function buildBrandFilterPageJsonLd(brand: { name: string; slug: string }, pageName: string, description: string, items: PartsListedItem[], dateModified?: string | null) {
  const page: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageName,
    description,
    url: `${SITE_URL}${FILTERS_ROUTE_ROOT}/${brand.slug}`,
    inLanguage: 'en-US',
    ...datedNode(dateModified),
    publisher: ORGANIZATION,
    about: { '@type': 'Brand', name: brand.name },
  }
  if (items.length === 0) return page
  return {
    ...page,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.title,
        url: `${SITE_URL}${filterPagePath(p.slug)}`,
      })),
    },
  }
}
